<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import comment from '@components/Comment.vue'
    import InputField from '@components/InputField.vue'
    import { nextTick, onMounted, ref } from 'vue'

    const props = defineProps({
        items: {
            type: Array,
            required: false
        },
        isOwner: {
            type: Boolean,
            required: false,
            default () {
                return false
            }
        },
        userId: {
            type: Number,
            required: false
        },
        type: {
            type: Number,
            required: true
        },
        contextualId: {
            type: Number,
            required: true
        },
        translations: {
            type: Object,
            required: false
        },
        apiToken: {
            type: String,
            required: false
        }
    });

    const editingPost = ref(true);

    const editingId = ref(null);

    const title = ref('');

    const content = ref('');

    const errors = ref(null);

    const posts = ref({});

    const loading = ref(true);

    const getPosts = () => {
        loading.value = true;
        axios.get(`/api/post?api_token=` + props.apiToken, {
            params: {
                'user_id': props.userId,
                'contextual_id': props.contextualId,
                'type': props.type
            }
        }).then(response => {
            if (response.data) {
                posts.value = response.data;
            }
            loading.value = false;
        }).catch(caughtErrors => {
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const editPost = (post) => {
        errors.value = null;
        editingPost.value = true;
        props.type = post.type;
        props.contextualId = post.contextual_id;
        title.value = post.title;
        content.value = post.content;
        editingId.value = post.id;
        
        nextTick(() => {
            const yOffset = -120; 
            const element = this.$refs.editor;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({top: y, behavior: 'smooth'});
        });

    }

    const savePost = () => {
        let postData = {
            'title': title.value,
            'content': content.value,
            'user_id': props.userId,
            'type': props.type,
            'contextual_id': props.contextualId
        };

        if (editingId.value) {
            postData.editing_id = editingId.value;
        }

        axios.post('/api/post?api_token=' + props.apiToken, postData).then(response => {
            if (response.data) {
                clearEdit();
                posts.value = response.data;
            }
            loading.value = false;
        }).catch(caughtErrors => {
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const deletePost = (postId, index) => {
        axios.delete(`/api/post/${postId}?api_token=` + props.apiToken
            ).then(response => {
            posts.value.splice(index, 1);
            loading.value = false;
        }).catch(caughtErrors => {
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const clearEdit = () => {
        editingPost.value = false;
        editingId.value = null;
        title.value = '';
        content.value = '';
    }

    onMounted(() => {
        if (props.items) {
            posts.value = props.items;
            loading.value = false;
        } else {
            getPosts();
        }
    });
</script>

<template>
    <div>
        <template v-if="isOwner">
            <button v-if="!editingPost" type="button" class="btn btn-success btn-md" style="margin-bottom: 10px;" @click="editingPost = true">{{ __('New post') }}</button>
            <div v-show="editingPost" class="row">
                <div class="col my-2" style="margin: 0 auto;">
                    <div class="card" ref="editor">
                        <div class="card-body">
                            <div class="clearfix"></div>
                            <InputField input-type="text" name="title" :label="__('Title')" :errors="errors" placeholder="" @update:field="title = $event" :data="title" />
                            <InputField input-type="textarea" name="content" :label="__('Content')" :errors="errors" :placeholder="__('What would you like to say?')" @update:field="content = $event" :data="content" />
                            <div class="float-right">
                                <button @click="savePost()" type="button" class="btn btn-success btn-md">
                                    <template v-if="!editingId">{{ __('Add post') }}</template>
                                    <template v-else>{{ __('Save post') }}</template>
                                </button>
                                <button type="button" class="btn btn-danger btn-md" style="margin-left: 10px; margin-right: 15px" @click="clearEdit()">{{ __('Cancel') }}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <div class="container">
            <div v-if="loading">
                <div class="loading-indicator"><div></div><div></div><div></div></div>
            </div>
            <template v-else>
                <div class="row">
                    <div v-show="post.id != editingId" class="col-xs-12 my-2" style="width: 100%; margin: 0 auto;" v-for="(post, index) in posts">
                        <div class="card">
                            <div class="card-body">
                                <div v-if="isOwner" style="margin-bottom: 20px;">
                                    <button @click="editPost(post)" type="button" class="btn btn-primary btn-md">
                                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="pen" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="width: 0.8rem; margin-bottom: 3px;"><path fill="currentColor" d="M290.74 93.24l128.02 128.02-277.99 277.99-114.14 12.6C11.35 513.54-1.56 500.62.14 485.34l12.7-114.22 277.9-277.88zm207.2-19.06l-60.11-60.11c-18.75-18.75-49.16-18.75-67.91 0l-56.55 56.55 128.02 128.02 56.55-56.55c18.75-18.76 18.75-49.16 0-67.91z"></path></svg>
                                    </button>
                                    <button @click="deletePost(post.id, index)" type="button" class="btn btn-danger btn-md float-right">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" style="width: 0.8rem; margin-bottom: 3px;"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z"/></svg>
                                    </button>
                                </div>
                                <read-more>
                                    <h3 class="card-title" v-html="post.title"></h3>
                                    <div v-html="post.content" style="margin: 20px 0;"></div>
                                </read-more>
                                <comment :contextual-id="post.id" :type="4" :user-id="userId ? userId : null"></comment>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>